import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams, Link } from "react-router-dom"
import { FaAngleLeft, FaAngleRight, FaPlus } from 'react-icons/fa'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/nav'
import { apiURL, filesURL, sitePath } from 'config/globals'
import Topbar from 'components/Topbar'
import CraftRecordNavigation from 'components/CraftRecordNavigation'
import FormRow from 'components/FormRow'
import CancelButton from 'components/CancelButton'
import SaveStepButton from 'components/SaveStepButton'
import Footer from 'components/Footer'
import { checkAuth } from 'utils/Auth'
import StickyFooter from "components/StickyFooter"
import { Button } from "react-bootstrap"

const productionPlaceOptions = [
    { value: 'Rural workshop', label: 'Rural workshop' },
    { value: 'Urban workshop', label: 'Urban workshop' },
    { value: 'Home', label: 'Home' },
    { value: 'Research Institution', label: 'Research Institution' },
    { value: 'Museum', label: 'Museum' },
]

const learningTypeOptions = [
    { value: 'Formal', label: 'Formal' },
    { value: 'Informal', label: 'Informal' },
]

const CraftRecord4 = () => {
    const [currentUser, setCurrentUser] = useState()
    const [museum, setMuseum] = useState({})
    const [craftRecord, setCraftRecord] = useState({
        type: 'traditional'
    })
    const [form, setForm] = useState()
    const [loadError, setLoadError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')

    const navigate = useNavigate()
    const { entityId, craftRecordId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
            if (craftRecordId) {
                await getCraftRecord(craftRecordId)
            }
        }
        start()
    }, [])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getCraftRecord = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-craft-record/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load record data: " + (response.data.error || response.data))
                return
            }

            setCraftRecord(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
    }

    const save = async () => {
        setSubmitError('')
        setIsSubmitting(true)

        const submittedForm = {...form}
        
        try {
            const response = await axios.put(`${apiURL}/update-craft-record/${craftRecord.id}`, submittedForm)
            
            if (!response.data.success) {
                setSubmitError(response.data.error || response.data)
                setIsSubmitting(false)
                return
            }

            setIsSubmitting(false)
        } catch (error) {
            setSubmitError(JSON.stringify(error))
            setIsSubmitting(false)
        }
    }

    const previousPage = () => {
        navigate(`/museum/${museum.id}/documentation/craft-records/${craftRecord.id}/edit-context`)
    }

    const nextPage = () => {
        navigate(`/museum/${museum.id}/documentation/craft-records/${craftRecord.id}/edit-materials`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width" style={{paddingBottom: 300, height: 'fit-content'}}>
                {museum.id &&
                    <>
                        <div>
                            <Link to={`/museum/${museum.id}/documentation/craft-records`} className="no-decoration"><FaAngleLeft /> Back to Craft Records</Link>
                        </div>
                        <header className="mt-2">
                            <div className="float-start">
                                <h1>Craft Record</h1>
                            </div>
                        </header>
                        <CraftRecordNavigation currentTab="person" museumId={museum.id} craftRecordId={craftRecord.id} />
                        {(!craftRecordId || craftRecord.id) &&
                            <>
                                <FormRow
                                    key="craftspersonName"
                                    label="Craftsperson name"
                                    content={
                                        <Form.Control
                                            name="craftspersonName"
                                            placeholder="Write the name of the craftsperson"
                                            defaultValue={craftRecord.craftspersonName}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                <FormRow
                                    key="craftspersonRole"
                                    label="Craftsperson role"
                                    content={
                                        <Form.Control
                                            name="craftspersonRole"
                                            placeholder="Write the role of the craftsperson"
                                            defaultValue={craftRecord.craftspersonRole}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                <FormRow
                                    key="productionLocality"
                                    label="Production locallity"
                                    content={
                                        <Form.Control
                                            name="productionLocality"
                                            placeholder="Write the geographical place or region of the workshop"
                                            defaultValue={craftRecord.productionLocality}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                <FormRow
                                    key="productionPlace"
                                    label="Place of production"
                                    content={
                                        <Form.Select
                                            name="productionPlace"
                                            defaultValue={craftRecord.productionPlace}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        >
                                            {productionPlaceOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))
                                            }
                                        </Form.Select>
                                    }
                                />
                                <FormRow
                                    key="learningMethodType"
                                    label="Method of learning the craft"
                                    content={
                                        <Form.Select
                                            name="learningMethodType"
                                            defaultValue={craftRecord.learningMethodType}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        >
                                            {learningTypeOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))
                                            }
                                        </Form.Select>
                                    }
                                />
                                <FormRow
                                    key="teachingMethodType"
                                    label="Method of teaching the craft"
                                    content={
                                        <Form.Select
                                            name="teachingMethodType"
                                            defaultValue={craftRecord.teachingMethodType}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        >
                                            {learningTypeOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))
                                            }
                                        </Form.Select>
                                    }
                                />
                                {submitError && 
                                    <Alert key='submitError' variant='danger'>
                                        {submitError}
                                    </Alert>
                                }
                            </>
                        }
                    </>
                }
                {loadError &&
                    <>
                        <Alert key='loadError' variant='danger'>
                            {loadError}
                        </Alert>
                    </>
                }
                <StickyFooter>
                    <Button size="lg" variant="outline-primary" className="float-start" onClick={previousPage}>
                        <FaAngleLeft /> Previous
                    </Button>
                    <Button size="lg" variant="primary" className="float-end" onClick={nextPage}>
                        Next <FaAngleRight />
                    </Button>
                </StickyFooter>
            </main>
        </>
    )
}

export default CraftRecord4